import type { Scalars, shipments, shipments_bool_exp, shipmentsRequest } from '$lib/tools/graphdb';
import { create_store } from '$lib/tools/graph-db/stores/create_store.svelte';
import { graphql } from '$lib/tools/graphdb';
import { balance_state } from '$lib/features/profile/states/balance_state';

export type Shipment = shipments;
export type ShipmentProduct = Shipment['Products'][number];
export type ShipmentProductExtra = ShipmentProduct['Product_extras'][number];
export type ProductItem = Shipment['Products'][number];
export type Product = ProductItem['Product'];

/**
 * Return fields for every shipments read (query, subscription, mutation).
 * Typed against the generated `shipmentsRequest` so the nested array
 * relationship tuple (`Products: [{ order_by }, { fields }]`) keeps its
 * exact shape. Consumers depend on this via `utils.return_fields`.
 */
const SHIPMENT_FIELDS: shipmentsRequest = {
	id: true,
	is_active: true,
	shipment_number: true,
	ready_time: true,
	date_created: true,
	cancellation_object: true,
	products_cost: true,
	required_amount: true,
	amount_paid: true,
	route_id: true,
	date_updated: true,
	shipping_cost: true,
	status: true,

	Buyer: {
		id: true,
		image_id: true,
		has_whatsapp: true,
		name: true,
		phone_number: true
	},

	Undefined_buyer: {
		id: true,
		name: true,
		has_whatsapp: true,
		phone_number: true
	},

	Destination_address: {
		id: true,
		name: true,
		city: true,
		description: true,
		verification_status: true,
		location: true
	},

	Boxes: {
		id: true,
		name: true,
		price: true,
		quantity: true,
		dimensions_cm: true,
		volume_cm3: true,
		Standard_size: {
			id: true,
			name: true,
			volume_cm3: true,
			length: true,
			width: true,
			height: true
		}
	},

	Products: [
		{ order_by: [{ date_created: 'desc' }] },
		{
			id: true,
			calculated_price: true,
			quantity: true,
			Product: {
				id: true,
				image_id: true,
				price: true,
				name: true,
				volume_cm3: true,
				dimensions_cm: true,
				Standard_size: {
					id: true,
					name: true,
					volume_cm3: true,
					length: true,
					width: true,
					height: true
				}
			},
			Product_extras: {
				id: true,
				quantity: true,
				calculated_price: true,
				Extra: {
					id: true,
					price: true,
					name: true
				}
			}
		}
	],

	Seller: {
		id: true,
		name: true,
		description: true,
		public_phone_number: true,
		image_id: true,
		Addresses: {
			location: true,
			description: true,
			city: true
		}
	},

	Notes: {
		id: true,
		created_at: true,
		role: true,
		user_id: true,
		note: true,
		shipment_id: true
	}
};

const shipments_store = create_store<shipments>({
	name: 'shipments',
	keys: { main: 'date_created', last_updated: 'date_updated' },
	fetch: {
		many: (opts) =>
			graphql.query_many(
				'shipments',
				opts?.where ? { where: opts.where as shipments_bool_exp } : undefined,
				SHIPMENT_FIELDS
			) as Promise<shipments[] | null>,
		one: (id) => graphql.query_one('shipments_by_pk', { id }, SHIPMENT_FIELDS) as Promise<shipments | null>
	},
	stream: (max) => (on_batch) =>
		graphql.subscribe(
			'shipments_stream',
			{ batch_size: 10, cursor: [{ initial_value: { date_updated: max } }] },
			SHIPMENT_FIELDS,
			(rows) => on_batch(rows),
			(error) => console.error('shipments_stream error', error)
		),
	auto: { populate: true, subscribe: true, prune: true }
});

// All-rows reactive view (RAM ∝ visible rows; liveQuery over the Dexie table).
const shipments_view = shipments_store.where({ index: 'date_created', sort: 'asc' });

const handlers = {
	cancellable_statuses: ['posted', 'scheduled', 'picked_up', 'reschedule'],
	cancellation_reasons: [
		'noshow',
		'buyer_cancelled',
		'seller_cancelled',
		'driver_cancelled',
		'ops_cancelled',
		'other'
	] as const,

	cancel_shipment: async ({
		shipment,
		reason,
		note,
		is_reschedule
	}: {
		shipment: shipments;
		reason: 'noshow' | 'buyer_cancelled' | 'seller_cancelled' | 'driver_cancelled' | 'other';
		note: string;
		is_reschedule: boolean;
	}): Promise<boolean> => {
		try {
			if (!handlers.cancellable_statuses.includes(shipment.status)) throw new Error('not allowed status');
			if (!handlers.cancellation_reasons.includes(reason)) throw new Error('not allowed reason');

			type InputPayload = {
				requested_status: Scalars['shipment_status'];
				amount_paid?: number;
				reason?: string;
				step_shipment_id?: string;
			};

			const payload: InputPayload = {
				requested_status: is_reschedule ? 'reschedule' : 'cancelled',
				reason: `-reason: ${reason}:\n-note: ${note ?? 'no_note'}`
			};

			const cancel_op = (await graphql.mutate(
				'request_shipment_status_change',
				{
					shipment_id: shipment.id,
					return_fields: SHIPMENT_FIELDS,
					payload
				},
				{ data: true, error: true }
			)) as { data?: unknown; error?: unknown } | null;

			const cancelled_shipment = cancel_op?.data as shipments;
			if (!cancelled_shipment?.id) return false;

			await Promise.all([
				shipments_store.sync.refetch(shipment.id),
				balance_state.fetch_balance(),
				shipment.route_id
					? (await import('./routes_state')).routes_state.fetch_one(shipment.route_id)
					: Promise.resolve()
			]);

			return true;
		} catch (error) {
			console.error({ error });
			return false;
		}
	}
};

export const shipments_state = {
	...shipments_store,

	get value(): shipments[] {
		return shipments_view.values;
	},

	get values(): shipments[] {
		return shipments_view.values;
	},

	/** Refetch a single shipment from the server and persist it locally. */
	fetch_one: (id: string): Promise<shipments | null> =>
		shipments_store.sync.refetch(id).then(() => shipments_store.get(id).value ?? null),

	...handlers,

	utils: {
		return_fields: SHIPMENT_FIELDS
	}
};
